import type { Family, FamilyFormErrors, FamilyFormValues, FamilyPayload, ParentOption, ParentSlot } from "../types";
import { resolveEntityId, sameId } from "./id";

const slots: ParentSlot[] = ["parent1", "parent2"];

export const createInitialFamilyFormValues = (): FamilyFormValues => ({
  familyName: "",
  familySituation: "MARRIED",
  selectedParents: {
    parent1: null,
    parent2: null,
  },
  search: {
    parent1: "",
    parent2: "",
  },
});

const parentNameFromFamily = (family: Family | null | undefined, slot: ParentSlot): ParentOption | null => {
  const id = family?.[`${slot}Id`];
  const uuid = family?.[`${slot}Uuid`];
  const firstName = family?.[`${slot}FirstName`];
  const lastName = family?.[`${slot}LastName`];
  if (!id && !uuid && !firstName && !lastName) return null;
  return {
    id: id ?? uuid ?? "",
    uuid,
    firstName,
    lastName,
    email: family?.[`${slot}Email`],
    phone: family?.[`${slot}Phone`],
  };
};

export const mapFamilyToFormValues = (family: Family | null | undefined, parents: ParentOption[] = []): FamilyFormValues => {
  const initial = createInitialFamilyFormValues();
  if (!family) return initial;

  const selectedParents = slots.reduce<FamilyFormValues["selectedParents"]>(
    (acc, slot) => {
      const slotId = family[`${slot}Id`] ?? family[`${slot}Uuid`];
      acc[slot] =
        parents.find((parent) => sameId(parent.id, slotId) || sameId(parent.uuid, slotId)) ??
        parentNameFromFamily(family, slot);
      return acc;
    },
    { parent1: null, parent2: null }
  );

  return {
    familyName: family.familyName ?? "",
    familySituation: family.familySituation ?? "MARRIED",
    selectedParents,
    search: initial.search,
  };
};

export const mapFormToFamilyPayload = (values: FamilyFormValues): FamilyPayload => ({
  familyName: values.familyName.trim(),
  parent1Id: resolveEntityId(values.selectedParents.parent1),
  parent2Id: resolveEntityId(values.selectedParents.parent2),
  familySituation: values.familySituation,
});

export const validateFamilyForm = (values: FamilyFormValues): FamilyFormErrors => {
  const errors: FamilyFormErrors = {};
  if (!values.familyName.trim()) errors.familyName = "required";
  if (!values.selectedParents.parent1) errors.parent1 = "required";
  if (!values.familySituation) errors.familySituation = "required";
  return errors;
};

export const hasFamilyFormErrors = (errors: FamilyFormErrors): boolean => Object.values(errors).some(Boolean);

export const parentDisplayName = (parent: ParentOption | null | undefined): string =>
  [parent?.firstName, parent?.lastName].filter(Boolean).join(" ").trim();

export const filterParentOptions = (parents: ParentOption[], query: string): ParentOption[] => {
  const term = query.trim().toLowerCase();
  if (!term) return [];
  return parents.filter((parent) => {
    const firstName = (parent.firstName ?? "").toLowerCase();
    const lastName = (parent.lastName ?? "").toLowerCase();
    const email = (parent.email ?? "").toLowerCase();
    return firstName.includes(term) || lastName.includes(term) || email.includes(term);
  });
};
